/* eslint-disable max-lines */
import React, {
    useContext, useState, useRef, useEffect
} from 'react';
import {
    useNavigate, useParams
} from 'react-router-dom';
import {
    Sparkles, Building2, ArrowRight,
    Layout, Settings, LogOut, Sun, Moon, User, Check, Menu, X
} from 'lucide-react';
import {
    Context
} from 'shared/context/GlobalState';
import {
    authService
} from 'shared/api';
import {
    APP_STUDIO_ENABLED
} from 'shared/config/featureFlags';
import StudioProductTabs from 'shared/components/StudioProductTabs';
import StudioOrgPills from 'shared/components/StudioOrgPills';
import coolriots from 'assets/coolriots.png';

/**
 * DSPlatformBar — single elegant top bar for DE Studio context.
 *
 * Replaces both the global platform header and the sub-org sidebar while the
 * user is inside DE Studio. Six concepts collapsed into three visual zones:
 *
 *   left   — BeX brand · Org pill ▾ · Sub-org pill ▾   (org/sub-org settings
 *            tucked into the dropdown footers)
 *   center — Product tabs: DE Studio (active) · App Builder
 *   right  — Theme toggle · user menu
 *
 * Always visible inside DE Studio (including the gate page), so the user can
 * jump to App Builder, Settings, or another sub-org without first picking a
 * mode. Per design rule "question containers when sparse" — sidebar removed.
 */
export default function DSPlatformBar() {
    const navigate = useNavigate();
    const {
        oragID, subOragID
    } = useParams();
    const {
        organizations
    } = useContext(Context);
    const [mobileOpen, setMobileOpen] = useState(false);

    const currentOrg = organizations?.find(o => o._id === oragID);

    return (
        <>
            <div
                className="ds-platform-bar"
                style={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 30,
                    height: 56,
                    background: 'var(--ds-bg)',
                    borderBottom: '1px solid var(--ds-divider)',
                    display: 'grid',
                    gridTemplateColumns: '1fr auto 1fr',
                    alignItems: 'center',
                    padding: '0 20px',
                    columnGap: 16
                }}
            >
                {/* ── Left column: brand + workspace context ──────────── */}
                <div className="ds-platform-bar-left" style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    minWidth: 0,
                    justifyContent: 'flex-start'
                }}>
                    {/* Mobile hamburger — visible only at <768px via CSS */}
                    <button
                        type="button"
                        className="ds-icon-btn ds-mobile-menu-btn"
                        onClick={() => setMobileOpen(true)}
                        aria-label="Open navigation menu"
                        title="Menu"
                    >
                        <Menu size={18} />
                    </button>
                    <BrandMark onClick={() => navigate('/organizations')} />
                    <ContextDivider className="ds-platform-bar-context" />
                    <div className="ds-platform-bar-context" style={{
                        display: 'contents'
                    }}>
                        <StudioOrgPills />
                    </div>
                </div>

                {/* ── Center column: product tabs (truly centered) — hidden on mobile ───── */}
                <div className="ds-platform-bar-center" style={{
                    display: 'flex',
                    justifyContent: 'center'
                }}>
                    {subOragID && <StudioProductTabs />}
                </div>

                {/* ── Right column: utilities ─────────────────────────── */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    gap: 4
                }}>
                    <UtilityCluster />
                </div>
            </div>
            {mobileOpen && (
                <MobileNavDrawer
                    onClose={() => setMobileOpen(false)}
                    currentOrg={currentOrg}
                    organizations={organizations}
                    oragID={oragID}
                    subOragID={subOragID}
                />
            )}
        </>
    );
}

/* ============================================================
 *                Mobile drawer (hamburger menu)
 * ============================================================
 * Slides in from the left at <768px. Carries everything we
 * hide from the platform bar at narrow widths: org switcher,
 * sub-org switcher, product tabs (DE Studio · App Builder).
 */
function MobileNavDrawer({
    onClose, currentOrg, organizations, oragID, subOragID
}) {
    const navigate = useNavigate();
    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);

    function go(path) {
        onClose();
        navigate(path);
    }

    const path = window.location.pathname;
    const onDeStudio = path.includes('/de-studio');
    const onAppBuilder = path.includes('/3dapp-studio');
    const subBase = subOragID
        ? `/organizations/${oragID}/sub/${subOragID}`
        : null;

    return (
        <>
            <div
                onClick={onClose}
                style={{
                    position: 'fixed',
                    inset: 0,
                    background: 'rgba(20, 20, 30, 0.40)',
                    backdropFilter: 'blur(2px)',
                    zIndex: 40
                }}
            />
            <div
                role="dialog"
                aria-label="Navigation"
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    bottom: 0,
                    width: 'min(82vw, 320px)',
                    background: 'var(--ds-surface)',
                    borderRight: '1px solid var(--ds-border)',
                    boxShadow: 'var(--ds-shadow-lg)',
                    zIndex: 41,
                    display: 'flex',
                    flexDirection: 'column',
                    animation: 'ds-slide-in-left var(--ds-dur) var(--ds-ease)'
                }}
            >
                {/* Drawer header */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '14px 16px',
                    borderBottom: '1px solid var(--ds-divider)'
                }}>
                    <span style={{
                        fontWeight: 700,
                        fontSize: 'var(--ds-text-md)',
                        color: 'var(--ds-text)'
                    }}>
                        Navigation
                    </span>
                    <button
                        type="button"
                        className="ds-icon-btn"
                        onClick={onClose}
                        aria-label="Close menu"
                    >
                        <X size={16} />
                    </button>
                </div>

                {/* Drawer body */}
                <div style={{
                    flex: 1,
                    overflowY: 'auto',
                    padding: 12
                }}>
                    {/* Organization */}
                    <DrawerSectionLabel>Organization</DrawerSectionLabel>
                    {(organizations || []).length === 0 ? (
                        <div style={{
                            padding: '8px 10px',
                            fontSize: 'var(--ds-text-sm)',
                            color: 'var(--ds-text-muted)'
                        }}>
                            No organizations yet.
                        </div>
                    ) : (
                        organizations.map(o => (
                            <button
                                key={o._id}
                                type="button"
                                onClick={() => go(`/organizations/${o._id}`)}
                                className="ds-context-row"
                                style={{
                                    minHeight: 40
                                }}
                            >
                                <Building2 size={14} style={{
                                    color: 'var(--ds-primary)'
                                }} />
                                <span style={{
                                    flex: 1,
                                    minWidth: 0,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                }}>
                                    {o.name}
                                </span>
                                {o._id === oragID && (
                                    <Check size={14} style={{
                                        color: 'var(--ds-primary)'
                                    }} />
                                )}
                            </button>
                        ))
                    )}

                    {/* Sub-organization */}
                    {currentOrg && (
                        <>
                            <DrawerSectionLabel>Sub-organization</DrawerSectionLabel>
                            {(currentOrg.subOrganizations || []).length === 0 ? (
                                <div style={{
                                    padding: '8px 10px',
                                    fontSize: 'var(--ds-text-sm)',
                                    color: 'var(--ds-text-muted)'
                                }}>
                                    No sub-organizations.
                                </div>
                            ) : (
                                currentOrg.subOrganizations.map(s => (
                                    <button
                                        key={s._id}
                                        type="button"
                                        onClick={() => go(`/organizations/${oragID}/sub/${s._id}`)}
                                        className="ds-context-row"
                                        style={{
                                            minHeight: 40
                                        }}
                                    >
                                        <Building2 size={14} style={{
                                            color: 'var(--ds-text-muted)'
                                        }} />
                                        <span style={{
                                            flex: 1,
                                            minWidth: 0,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap'
                                        }}>
                                            {s.name}
                                        </span>
                                        {s._id === subOragID && (
                                            <Check size={14} style={{
                                                color: 'var(--ds-primary)'
                                            }} />
                                        )}
                                    </button>
                                ))
                            )}
                        </>
                    )}

                    {/* Product tabs */}
                    {subBase && (
                        <>
                            <DrawerSectionLabel>Workspace</DrawerSectionLabel>
                            <button
                                type="button"
                                onClick={() => go(`${subBase}/de-studio`)}
                                className="ds-context-row"
                                style={{
                                    minHeight: 40,
                                    fontWeight: onDeStudio ? 600 : 400,
                                    color: onDeStudio ? 'var(--ds-primary)' : 'var(--ds-text)'
                                }}
                            >
                                <Sparkles size={14} />
                                <span style={{
                                    flex: 1
                                }}>DE Studio</span>
                                {onDeStudio && <Check size={14} style={{
                                    color: 'var(--ds-primary)'
                                }} />}
                            </button>
                            {APP_STUDIO_ENABLED && (
                                <button
                                    type="button"
                                    onClick={() => go(`/organizations/${oragID}/3dapp-studio`)}
                                    className="ds-context-row"
                                    style={{
                                        minHeight: 40,
                                        fontWeight: onAppBuilder ? 600 : 400,
                                        color: onAppBuilder ? 'var(--ds-primary)' : 'var(--ds-text)'
                                    }}
                                >
                                    <Layout size={14} />
                                    <span style={{
                                        flex: 1
                                    }}>3D App Studio</span>
                                    {onAppBuilder && <Check size={14} style={{
                                        color: 'var(--ds-primary)'
                                    }} />}
                                </button>
                            )}
                        </>
                    )}

                    {/* Settings shortcut */}
                    {oragID && (
                        <>
                            <DrawerSectionLabel>Settings</DrawerSectionLabel>
                            <button
                                type="button"
                                onClick={() => go(`/organizations/${oragID}/settings`)}
                                className="ds-context-row"
                                style={{
                                    minHeight: 40
                                }}
                            >
                                <Settings size={14} />
                                <span style={{
                                    flex: 1
                                }}>Manage organization</span>
                                <ArrowRight size={12} style={{
                                    color: 'var(--ds-text-subtle)'
                                }} />
                            </button>
                            {subOragID && (
                                <button
                                    type="button"
                                    onClick={() => go(`/organizations/${oragID}/sub/${subOragID}/settings`)}
                                    className="ds-context-row"
                                    style={{
                                        minHeight: 40
                                    }}
                                >
                                    <Settings size={14} />
                                    <span style={{
                                        flex: 1
                                    }}>Sub-organization settings</span>
                                    <ArrowRight size={12} style={{
                                        color: 'var(--ds-text-subtle)'
                                    }} />
                                </button>
                            )}
                        </>
                    )}
                </div>
            </div>
        </>
    );
}

function DrawerSectionLabel({
    children
}) {
    return (
        <div style={{
            padding: '12px 10px 4px',
            fontSize: 'var(--ds-text-2xs)',
            fontWeight: 600,
            color: 'var(--ds-text-subtle)',
            textTransform: 'uppercase',
            letterSpacing: '0.06em'
        }}>
            {children}
        </div>
    );
}

/* ============================================================
 *                       Brand mark
 * ============================================================ */
function BrandMark({
    onClick
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            className="ds-brand-btn"
            aria-label="CoolRIOTS — go to organizations"
        >
            <img
                src={coolriots}
                alt="CoolRIOTS"
                style={{
                    height: 22,
                    width: 'auto',
                    display: 'block'
                }}
            />
        </button>
    );
}

function ContextDivider({
    className
}) {
    return (
        <div className={className} style={{
            width: 1,
            height: 22,
            background: 'var(--ds-divider)',
            flexShrink: 0,
            marginLeft: 4,
            marginRight: 4
        }} />
    );
}


/* ============================================================
 *                       Right: utilities
 * ============================================================ */
function UtilityCluster() {
    return (
        <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4
        }}>
            <DSThemeToggle />
            <UserMenu />
        </div>
    );
}

function DSThemeToggle() {
    const isDark = typeof document !== 'undefined' && document.documentElement.classList.contains('dark');
    const [dark, setDark] = useState(isDark);
    const toggle = () => {
        document.documentElement.classList.toggle('dark');
        setDark(d => !d);
    };
    return (
        <button
            type="button"
            onClick={toggle}
            className="ds-icon-btn"
            aria-label={dark ? 'Switch to light theme' : 'Switch to dark theme'}
            title={dark ? 'Light theme' : 'Dark theme'}
        >
            {dark ? <Sun size={15} /> : <Moon size={15} />}
        </button>
    );
}

function UserMenu() {
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        if (!open) return;
        const onDoc = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
        const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('mousedown', onDoc);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDoc);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    const handleLogout = async () => {
        try { await authService.logout(); } catch { /* ignore */ }
        navigate('/auth/login');
    };

    return (
        <div ref={ref} style={{
            position: 'relative'
        }}>
            <button
                type="button"
                onClick={() => setOpen(o => !o)}
                className="ds-avatar-btn"
                aria-haspopup="menu"
                aria-expanded={open}
            >
                <span style={{
                    width: 28,
                    height: 28,
                    borderRadius: 'var(--ds-radius-full)',
                    background: 'var(--ds-primary-soft)',
                    color: 'var(--ds-primary)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 700,
                    fontSize: 'var(--ds-text-xs)'
                }}>
                    <User size={13} />
                </span>
            </button>
            {open && (
                <div role="menu" style={{
                    position: 'absolute',
                    top: 'calc(100% + 6px)',
                    right: 0,
                    minWidth: 200,
                    padding: 4,
                    background: 'var(--ds-surface)',
                    border: '1px solid var(--ds-border)',
                    borderRadius: 'var(--ds-radius-lg)',
                    boxShadow: 'var(--ds-shadow-lg)',
                    zIndex: 60,
                    animation: 'ds-pop-in var(--ds-dur-fast) var(--ds-ease)'
                }}>
                    <button type="button" onClick={() => { navigate('/organizations'); setOpen(false); }} className="ds-context-row">
                        <Building2 size={13} /> All organizations
                    </button>
                    <div style={{
                        height: 1,
                        background: 'var(--ds-divider)',
                        margin: '4px 4px'
                    }} />
                    <button type="button" onClick={handleLogout} className="ds-context-row" style={{
                        color: 'var(--ds-danger)'
                    }}>
                        <LogOut size={13} /> Sign out
                    </button>
                </div>
            )}
        </div>
    );
}
